import React, { Component, createRef } from "react";
import i18n from "i18next";
import PropTypes from "prop-types";
import {
  scrollElement,
  findBox,
  blurCKEditor,
  fontString,
  safeDecodeURI,
  normalizeFontSizes,
  injectBoxFontSize,
  deriveTextFormatCached,
} from "../../../../common/common_tools";

// Every seed of the editor goes through this: decode, normalize px→em, then
// inject the box-level font size as a content span (see injectBoxFontSize) so
// the editing view is single-layer and sizes picked inside CKEditor render at
// their true value instead of compounding with the wrapper's em.
function seedData(text, toolbarState) {
  return injectBoxFontSize(
    normalizeFontSizes(safeDecodeURI(text)),
    effectiveFontSize(toolbarState, text)
  );
}

// Box-state size, else the size the content carries — keeps the dropdown, the
// pill and the canvas in agreement.
function effectiveFontSize(toolbarState, text) {
  const state = toolbarState || {};
  return state.textFontSize || deriveTextFormatCached(text).textFontSize || undefined;
}
import {
  SUPPORTED_GOOGLE_FONTS,
  LOCAL_FONTS,
  generateGoogleFontsLink,
} from "../../../../common/font_extractor.es6";

export default class CKEDitorComponent extends Component {
  constructor(props) {
    super(props);
    this.onBlur = this.onBlur.bind(this);
    this.textareaRef = createRef();
  }

  onBlur() {
    blurCKEditor(this.props.id, this.props.onBlur);
  }

  render() {
    return (
      <div
        id={this.props.id}
        ref={this.textareaRef}
        className={this.props.className}
        contentEditable="true"
        style={this.props.style}
      />
    );
  }

  componentDidMount() {
    let toolbar = this.props.toolbars[this.props.id];
    let config = IdealStudio.Plugins.get(toolbar.pluginId)?.getConfig();
    if (config && config.needsTextEdition) {
      CKEDITOR.disableAutoInline = true;
      // Destroy any stale instance bound to this id before re-attaching. Without
      // this, a remount (e.g. answer rows re-rendering during reorder) throws
      // "The editor instance is already attached to the provided element."
      if (CKEDITOR.instances[this.props.id]) {
        try {
          CKEDITOR.instances[this.props.id].destroy(true);
        } catch (e) {
          /* ignore — proceed to re-attach */
        }
      }
      // CKEDITOR.addCss('p span{font-size: 1vw} p{font-size: 1vw}');
      /* for (let key in config.extraTextConfig) {
                CKEDITOR.config[key] += toolbar.config.extraTextConfig[key] + ",";
            }*/
      let editor = CKEDITOR.inline(this.textareaRef.current);

      /* editor.document.$.body.disabled = true;
            editor.document.$.body.contentEditable = false;
            editor.document.$.designMode="off";*/

      editor.config.fontSize_sizes = fontString();
      const seedState = (toolbar && toolbar.state) || {};
      const derived = deriveTextFormatCached(seedState.__text);
      editor.config.font_defaultLabel =
        seedState.textFontFamily || derived.textFontFamily || "Arial";
      const seedSize = effectiveFontSize(seedState, seedState.__text);
      editor.config.fontSize_defaultLabel = seedSize ? String(seedSize) : "14";

      // Dynamically load all supported and local fonts into the CKEditor dropdown
      const defaultFonts =
        "Arial/Arial, Helvetica, sans-serif;" +
        "Comic Sans MS/Comic Sans MS, cursive;" +
        "Courier New/Courier New, Courier, monospace;" +
        "Georgia/Georgia, serif;" +
        "Lucida Sans Unicode/Lucida Sans Unicode, Lucida Grande, sans-serif;" +
        "Tahoma/Tahoma, Geneva, sans-serif;" +
        "Times New Roman/Times New Roman, Times, serif;" +
        "Trebuchet MS/Trebuchet MS, Helvetica, sans-serif;" +
        "Verdana/Verdana, Geneva, sans-serif;";

      const googleFontsStr = SUPPORTED_GOOGLE_FONTS.map((f) => `${f}/${f}, sans-serif`).join(";");
      const localFontsStr = LOCAL_FONTS.map((f) => `${f}/${f}, sans-serif`).join(";");

      editor.config.font_names = `${defaultFonts}${googleFontsStr};${localFontsStr}`;

      // BasicText keeps CKEditor's native floating toolbar too (same as
      // RichText). It only shows while the full editor is explicitly opened —
      // right rail "Metni Düzenle" or double-click — so it doesn't compete with
      // the over-box pill's whole-box formatting. It used to be hidden
      // (toolbar = []), which made opening the editor visually undetectable:
      // no chrome appeared and users read it as "the editor doesn't open".

      // editor.config.fontSize_style = {
      //     element:        'span',
      //     styles:         { 'font-size': '#(size)' },
      //     overrides:      [ { element: 'font', attributes: { 'size': null } } ]
      // };
      if (toolbar.state.__text) {
        editor.setData(seedData(toolbar.state.__text, toolbar.state));
      }
      // Baseline for blurCKEditor's no-edit check: getData() right after
      // seeding IS "unchanged" (normalize/CKEditor rewrites included), so an
      // open+close with zero typing commits nothing.
      editor.on("dataReady", () => {
        editor.__aideaOpenData = editor.getData().replace(/<!--[\s\S]*?-->/g, "");
      });

      // The size/font combos only recognise explicit <span> styles in the
      // CONTENT. Box-level formatting (the pill's textFontSize/textFontFamily)
      // lives on the wrapper, so with clean content the combos fell back to
      // their static defaults ("14" / "Arial") no matter what the text
      // actually shows. Runs after the plugin's own selectionChange handler
      // (priority 100 > default) and only fills the label when nothing matched.
      editor.on(
        "selectionChange",
        () => {
          try {
            const tb = this.props.toolbars[this.props.id];
            if (!tb || !tb.state) return;
            const sizeCombo = editor.ui.get("FontSize");
            if (sizeCombo && !sizeCombo.getValue()) {
              sizeCombo.setValue("", String(tb.state.textFontSize || 14));
            }
            const fontCombo = editor.ui.get("Font");
            if (fontCombo && !fontCombo.getValue() && tb.state.textFontFamily) {
              fontCombo.setValue("", tb.state.textFontFamily.split(",")[0].replace(/["']/g, ""));
            }
          } catch (e) {
            /* combos not rendered yet — labels stay default */
          }
        },
        null,
        null,
        100
      );

      // Automatically fetch missing Google Fonts instantly upon selection
      editor.on("change", () => {
        const html = editor.getData();
        const styleRegex = /font-family\s*:\s*(?:["']?([^"',;]+)["']?)/gi;
        let match;
        const missingGoogleFonts = new Set();

        while ((match = styleRegex.exec(html)) !== null) {
          const fontName = match[1].trim().replace(/["']/g, "");
          if (SUPPORTED_GOOGLE_FONTS.includes(fontName)) {
            const fontUrlName = fontName.replace(/ /g, "+");
            const linkExists = Array.from(
              document.querySelectorAll('link[href*="fonts.googleapis.com"]')
            ).some((l) => l.href.includes(fontUrlName));
            if (!linkExists) {
              missingGoogleFonts.add(fontName);
            }
          }
        }

        if (missingGoogleFonts.size > 0) {
          const fontsMap = new Map();
          missingGoogleFonts.forEach((f) => fontsMap.set(f, new Set([400, 700])));
          const linkHTML = generateGoogleFontsLink(fontsMap);
          if (linkHTML) {
            const linkUrl = linkHTML.match(/href=["']([^"']+)["']/)?.[1];
            if (linkUrl) {
              const linkElement = document.createElement("link");
              linkElement.rel = "stylesheet";
              linkElement.href = linkUrl;
              linkElement.setAttribute("data-editor-fonts", "true");
              document.head.appendChild(linkElement);
            }
          }
        }
      });

      // KİTLİ DÜZENLEME: Rich editör artık blur (click-away) ile otomatik
      // commit/kapanmıyor — metnin içine tıklamak/seçim yapmak editörü
      // kapatıp "kayma/flicker" yaratıyordu. Kapatma yalnızca kutunun sağ
      // üstündeki "Kaydet" butonuyla yapılır (EditorBox.saveRichEditor →
      // blurCKEditor). Eski auto-blur-commit kasıtlı olarak devre dışı.
      // editor.on("blur", () => { ... });

      editor.focus();

      // SAFARI FIX: Problema con CKEditor y Safari al intentar crear un editor inline sobre un div con display: none. Solución propuesta en: https://dev.ckeditor.com/ticket/9814
      let isSafari =
        /constructor/i.test(window.HTMLElement) ||
        (function (p) {
          return p.toString() === "[object SafariRemoteNotification]";
        })(!window.safari || (typeof safari !== "undefined" && safari.pushNotification));
      if (isSafari) {
        editor.on("focus", () => {
          editor.setReadOnly(false);
        });
      }
    }
  }

  componentWillUnmount() {
    if (CKEDITOR.instances[this.props.id]) {
      if (CKEDITOR.instances[this.props.id].focusManager.hasFocus) {
        this.onBlur();
      }
      CKEDITOR.instances[this.props.id].destroy();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Auto-save + close on a REAL deselect — i.e. Redux's boxSelected actually
    // moves to a different box/nothing, not just a native DOM blur. This is
    // deliberately narrower than the old (removed) auto-blur-commit, which
    // fired on ANY blur — including clicks on the CKEditor's own floating
    // toolbar/color-panel — and caused the "kayma/flicker" bug (the editor
    // closing mid-edit just from clicking its own UI). Pill-button clicks
    // don't touch boxSelected, so they can't trigger this. Removes the need
    // for an explicit "Kaydet" button — matches the pill's instant-apply
    // controls, which already commit without one.
    if (
      prevProps.boxSelected === prevProps.id &&
      this.props.boxSelected !== this.props.id &&
      prevProps.toolbars[prevProps.id] &&
      prevProps.toolbars[prevProps.id].showTextEditor
    ) {
      this.onBlur();
    }
    if (
      this.props.toolbars[this.props.id].showTextEditor &&
      prevProps.toolbars[prevProps.id] &&
      !prevProps.toolbars[prevProps.id].showTextEditor
    ) {
      this.textareaRef.current.focus();
      // Focus cursor at end of content
      // https://recalll.co/ask/v/topic/fckeditor-How-to-set-cursor-position-to-end-of-text-in-CKEditor/5541ec6304ce0209458b9107#59f908ff1126f4577eec64ec

      let myEditor = CKEDITOR.instances[this.props.id];
      // Obtain the current selection & range

      if (myEditor) {
        myEditor.setData(
          seedData(
            this.props.toolbars[this.props.id].state.__text,
            this.props.toolbars[this.props.id].state
          )
        );
        let range = myEditor.createRange();
        if (range && range.root) {
          range.moveToElementEditEnd(range.root);
          myEditor.getSelection().selectRanges([range]);
        }
        // $.event.trigger({ type : 'keypress' });
      }
      this.textareaRef.current.focus();
    }
    if (window.MathJax) {
      window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub]);
    }

    if (
      prevProps.box.parent !== this.props.box.parent ||
      prevProps.box.container !== this.props.box.container
    ) {
      for (let instance in CKEDITOR.instances) {
        CKEDITOR.instances[instance].destroy();
      }
      CKEDITOR.inlineAll();
      for (let editor in CKEDITOR.instances) {
        if (this.props.toolbars[editor].state.__text) {
          CKEDITOR.instances[editor].setData(
            seedData(this.props.toolbars[editor].state.__text, this.props.toolbars[editor].state)
          );
        }
      }
    }
  }
  componentWillReceiveProps(nextProps) {
    // Real deselect → auto-save is handled in componentDidUpdate (see there
    // for why). This hook only handles toggling INTO/OUT of edit mode while
    // still selected (e.g. the "edit_text" button or double-click).
    if (nextProps.boxSelected === nextProps.id) {
      if (
        this.props.toolbars[this.props.id].showTextEditor === true &&
        nextProps.toolbars[nextProps.id].showTextEditor === false
      ) {
        // this.onBlur();
      } else if (
        this.props.toolbars[this.props.id].showTextEditor === false &&
        nextProps.toolbars[nextProps.id].showTextEditor === true
      ) {
        let CKstring = CKEDITOR.instances[nextProps.id].getData();
        let initString = "<p>" + i18n.t("text_here") + "</p>\n";
        if (CKstring === initString) {
          CKEDITOR.instances[nextProps.id].setData("");
        } else {
          CKEDITOR.instances[nextProps.id].setData(
            seedData(
              nextProps.toolbars[nextProps.id].state.__text,
              nextProps.toolbars[nextProps.id].state
            )
          );
        }
      }
    }
  }
}
CKEDitorComponent.propTypes = {
  /**
   * Selected box
   */
  boxSelected: PropTypes.any,
  /**
   * Box to which the CKEDitor belongs
   */
  box: PropTypes.any,
  /**
   * Style object
   */
  style: PropTypes.object,
  /**
   * CSS classes to apply
   */
  className: PropTypes.string,
  /**
   * Toolbars
   */
  toolbars: PropTypes.object,
  /**
   * Box id
   */
  id: PropTypes.any,
  /**
   * Blur callback
   */
  onBlur: PropTypes.func.isRequired,
};
